import { UNLIMITED_AI_CREDITS_SENTINEL } from "@hex/common";

export interface FormatAiBillingEnforcementDateOptions {
  /** When true, appends the year (e.g. "May 19, 2026"). Default is long month + day only. */
  includeYear?: boolean;
}

/**
 * Formats `org.aiBillingEnforcementDate` for US UI copy. Returns null if missing or unparseable.
 *
 * Uses the UTC calendar date so the label matches ISO timestamps from the API and does not shift
 * by the viewer's local timezone (e.g. midnight UTC staying on the intended calendar day).
 */
export function formatAiBillingEnforcementDate(
  date: string | Date | null | undefined,
  options?: FormatAiBillingEnforcementDateOptions,
): string | null {
  if (date == null || (typeof date === "string" && date === "")) {
    return null;
  }
  const parsed = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  return parsed.toLocaleDateString("en-US", {
    timeZone: "UTC",
    month: "long",
    day: "numeric",
    ...(options?.includeYear ? { year: "numeric" as const } : {}),
  });
}

/**
 * True when the current moment is strictly before the start of the enforcement UTC calendar day
 * (aligned with {@link formatAiBillingEnforcementDate}). False if missing or invalid.
 */
export function isBeforeAiBillingEnforcementDate(
  date: Date | null | undefined,
): boolean {
  if (date == null) {
    return false;
  }
  if (Number.isNaN(date.getTime())) {
    return false;
  }
  const startOfEnforcementDayUtc = Date.UTC(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
  );
  return Date.now() < startOfEnforcementDayUtc;
}

/** e.g. "April 20th" for credit reset copy (viewer-local calendar date). */
export function formatCreditResetMonthAndOrdinalDay(date: Date): string {
  const month = date.toLocaleDateString("en-US", { month: "long" });
  const day = date.getDate();
  const ordinalSuffix =
    day % 10 === 1 && day !== 11
      ? "st"
      : day % 10 === 2 && day !== 12
        ? "nd"
        : day % 10 === 3 && day !== 13
          ? "rd"
          : "th";
  return `${month} ${day}${ordinalSuffix}`;
}

/** Rounds credit amounts for display (avoids float artifacts like 9.310000000000002). */
export const formatCreditsForDisplay = (value: number): string => {
  const formatted = new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 1,
    minimumFractionDigits: 0,
  }).format(value);
  // Use Intl’s rounded string, not a raw float threshold — e.g. 10.1 - 10.0 is ~0.099999… but formats as "0.1".
  if (value > 0 && formatted === "0") {
    return "< 0.1";
  }
  return formatted;
};

/**
 * Returns a number representing the fraction of credits used, where 0 represents 0% and 1 represents 100%
 * The returned value can exceed 1 if the user has used more credits than are available.
 */
export const getCreditUsagePercentage = (
  totalUserCreditsAvailable: number | typeof UNLIMITED_AI_CREDITS_SENTINEL,
  totalUserCreditsUsed: number,
): number => {
  // Handle the case where the user has unlimited credits, which should be treated as 0% used
  if (totalUserCreditsAvailable === UNLIMITED_AI_CREDITS_SENTINEL) {
    return 0;
  }
  // Handle the case where the user has no credits available, which should be treated as 100% used
  if (totalUserCreditsAvailable <= 0) {
    return 1;
  }
  return totalUserCreditsUsed / totalUserCreditsAvailable;
};
